/**
 * Bodies for report-service's `SCRIPT` export processor.
 *
 * report-service wraps each body as `function process(data) { <body> }` and runs it under Nashorn,
 * with `data` mapping every path in the column's `fieldNames` to its value's `toString()`, or null.
 * So every body here reads strings, and returns a string - a returned JS number reaches the CSV
 * through Java's own number formatting, which is not ours to rely on.
 *
 * Every path and table entry is embedded through `JSON.stringify`, so a translated label carrying a
 * quote cannot end the string early or change what the script does.
 */

function read(path: string): string {
	return `data[${JSON.stringify(path)}]`;
}

/** Numeric string -> `n` divided by `divisor`, 2 decimals, no trailing zeros. Empty for a missing
 * or non-numeric value. Expects `v` to be declared by the caller. The wire sends some of these as a
 * Java double, so `20.0` and a variance like `3.3333333333333335` both go through the rounding. */
function scaledFromV(divisor: number): string {
	return (
		'if (v === null || v === undefined || v === "") return ""; ' +
		'var n = Number(v); if (isNaN(n)) return ""; ' +
		`return String(Math.round(n / ${divisor} * 100) / 100);`
	);
}

/** Minutes string -> hours string. */
const HOURS_FROM_V = scaledFromV(60);
/** A value already in its own unit (points, or unitless), rounded the same way. */
const AS_UNIT_FROM_V = scaledFromV(1);

/** A `dataType: 'duration'` leaf: always minutes on the wire. */
export function hoursScript(path: string): string {
	return `var v = ${read(path)}; ${HOURS_FROM_V}`;
}

/** A unit-varying number leaf: minutes on an HOURS project, the point count on a POINTS project,
 * and - as on screen - the value as it stands when the row carries no unit. Points and unitless
 * values get the same 2-decimal rounding as hours. */
export function unitVaryingScript(path: string, unitPath: string): string {
	return `var v = ${read(path)}; var u = ${read(unitPath)}; if (u !== "HOURS") { ${AS_UNIT_FROM_V} } ${HOURS_FROM_V}`;
}

/** The unit column beside the unit-varying ones: `h`, `p`, or empty when the row has no unit. */
export function unitColumnScript(unitPath: string): string {
	return `var u = ${read(unitPath)}; return u === "HOURS" ? "h" : u === "POINTS" ? "p" : "";`;
}

/** The project pill's id, by the same rule as `shapeProject`: the custom id when set, else
 * `P<companyProjectId>`. Zero is not a project. */
export function projectIdScript(customIdPath: string, companyIdPath: string): string {
	return (
		`var c = ${read(customIdPath)}; if (c !== null && c !== undefined && c !== "") return String(c); ` +
		`var n = ${read(companyIdPath)}; ` +
		'if (n === null || n === undefined || n === "" || Number(n) === 0) return ""; ' +
		'return "P" + n;'
	);
}

/** Wire value -> display label. An unknown value is echoed, a missing one is empty. `hasOwnProperty`
 * rather than `in` so `toString` and friends do not answer. */
export function lookupScript(path: string, table: Record<string, string>): string {
	return (
		`var t = ${JSON.stringify(table)}; var v = ${read(path)}; ` +
		'if (v === null || v === undefined) return ""; ' +
		'return Object.prototype.hasOwnProperty.call(t, v) ? t[v] : String(v);'
	);
}
